import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from 'types'
import { AppDispatch } from 'store/types'
import { handlePacingUpload, onFileChange } from 'store/css-admin/pacing'
import type { PacingManagerState } from 'store/css-admin/pacing/reducer'

import Pacing from './Pacing'

export interface FormattedPacingResult {
  error?: string
  nodeFriendlyName?: string
  rowNumber?: number
  siteId: number | string
  siteName?: string
  startDate?: string
}

const PacingContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()

  const { file, fileName, isLoading, uploadResults } = useSelector<
    RootState,
    PacingManagerState
  >(state => state.pacingManager)

  const formattedResults = useMemo<FormattedPacingResult[] | null>(() => {
    if (!uploadResults) {
      return null
    }

    const { pacingDates: pacingResults = [], errors = [] } = uploadResults

    // Flatten pacing results into individual rows
    const formattedRows: FormattedPacingResult[] = pacingResults.flatMap(
      pacingResult =>
        pacingResult.pacingDates.map(pacingEntry => ({
          siteId: pacingResult.siteId,
          siteName: pacingResult.siteName,
          nodeFriendlyName: pacingEntry.nodeFriendlyName,
          startDate: new Date(pacingEntry.startDate).toLocaleDateString(),
        })),
    )

    // Prepend any errors to the beginning of results
    if (errors.length > 0) {
      const errorRows: FormattedPacingResult[] = errors.map(error => ({
        siteId: error.siteId,
        rowNumber: error.rowNumber,
        error: error.error,
      }))
      formattedRows.unshift(...errorRows)
    }

    return formattedRows.length > 0 ? formattedRows : null
  }, [uploadResults])

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(onFileChange(event))
    },
    [dispatch],
  )

  const handleUpload = useCallback(
    (file: File) => {
      dispatch(handlePacingUpload(file))
    },
    [dispatch],
  )

  return (
    <Pacing
      file={file}
      fileName={fileName}
      formattedResults={formattedResults}
      handlePacingUpload={handleUpload}
      isLoading={isLoading}
      onFileChange={handleFileChange}
    />
  )
}

export default PacingContainer
